import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { Box, Stack, Typography } from "@mui/material";

import { ConditionCard } from "@covertree/ui";
import { ConditionData, YesNo } from "@covertree/types";
import { booleanToYesNo, yesNoToBoolean } from "@covertree/utils";
import {
  resetRelatedFields,
  mapFormKeyToUnderwritingQuestion,
  MappedUnderwritingQuestion,
  UnderwritingQuestionsForm,
} from "@covertree/new-business-forms";

interface FormUnderwritingHomeOptionsProps {
  title?: string;
  questions: MappedUnderwritingQuestion[];
  setModalCondition(condition: ConditionData, hasAnswerChanged?: boolean): void;
}

export function FormUnderwritingHomeOptions({
  title,
  questions,
  setModalCondition,
}: FormUnderwritingHomeOptionsProps) {
  const { setValue, getValues } = useFormContext<UnderwritingQuestionsForm>();

  const selectHomeCondition = useCallback(
    (conditionKey: keyof UnderwritingQuestionsForm, value: boolean) => {
      if (!value) {
        resetRelatedFields(
          setValue,
          mapFormKeyToUnderwritingQuestion[conditionKey]
        );
      }

      setValue(conditionKey, booleanToYesNo(value), {
        shouldValidate: true,
        // endorsement flow uses it to track changes e.g. to trigger the mutation.
        shouldDirty: true,
      });
    },
    [setValue]
  );

  return (
    <Box>
      {title && (
        <Typography variant="h4" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      <Box>
        <Stack spacing={2}>
          {questions.filter(Boolean).map(({ key, value }) => {
            const formValue = getValues(key);
            const isSelected =
              formValue && formValue in YesNo
                ? yesNoToBoolean(formValue)
                : !!formValue;

            return (
              <Box key={key}>
                <ConditionCard
                  key={key}
                  isSelected={isSelected}
                  condition={value}
                  conditionKey={key}
                  onToggle={(conditionCardValue) =>
                    selectHomeCondition(key, conditionCardValue)
                  }
                  setModalCondition={setModalCondition}
                />
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}
